if (!customElements.get("quick-add")) {
  /* Product markup already fetched in this page view, keyed by product url.
     Holds either the html or the in-flight request. */
  const quickAddHtmlCache = new Map();

  /* The quick view only needs the main product section, not the whole product
     page (~85kb instead of ~500kb). The section id is only known once a product
     page has been parsed, so it is remembered for the rest of the session and
     every response is validated before it is used. */
  const QUICK_ADD_SECTION_KEY = "quickAddProductSectionId";

  const readSectionId = () => {
    try {
      return sessionStorage.getItem(QUICK_ADD_SECTION_KEY);
    } catch (error) {
      return null;
    }
  };

  const storeSectionId = (id) => {
    try {
      sessionStorage.setItem(QUICK_ADD_SECTION_KEY, id);
    } catch (error) {
      /* private mode - just keep fetching the full page */
    }
  };

  const quickAddSectionUrl = (url, sectionId) => {
    const [path, query = ""] = url.split("?");
    const params = new URLSearchParams(query);
    params.set("section_id", sectionId);
    return `${path}?${params.toString()}`;
  };

  /* The quick view shows the image at a fraction of the product page size, so
     ask the CDN for a matching one instead of the full size gallery image. */
  const quickAddImageUrl = (src, width) => {
    try {
      const url = new URL(src, window.location.origin);
      if (!url.pathname.includes("/files/") && !url.pathname.includes("/products/")) {
        return src;
      }
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  customElements.define(
    "quick-add",
    class QuickAdd extends HTMLElement {
      getFocusableElements(parent) {
        const focusableElementsSelector =
          "button:not(.wt-product__sticky-buy__button), [href], input:not(.disabled):not([type='hidden']), select, .close-btn";
        const focusableElements = () =>
          Array.from(parent.querySelectorAll(focusableElementsSelector)).filter(
            (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
          );

        return {
          focusableElements,
          first: focusableElements()[0],
          last: focusableElements()[focusableElements().length - 1],
        };
      }

      connectedCallback() {
        this.add_button = this.querySelector("button");
        this.isDrawerOpen = false;
        this.isMobile = window.innerWidth < 768;
          if (this.add_button)
            this.add_button.addEventListener("click", (e) => {
          const button = e.currentTarget;
          const cardLink = button
          .closest(".card__picture-container")
          ?.querySelector("a.card");
          this.product_url = this.add_button.getAttribute("data-product-url");
            this.layout = button.getAttribute("data-quick-add-layout");
            this.currentTrigger = cardLink;
            this.fetchProduct(this.product_url);
          });

        this.handleInteractionOutside =
          this.handleInteractionOutside.bind(this);

        this.getFocusableElements = this.getFocusableElements.bind(this);

        this.handleClose = this.handleClose.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleOptionClick = this.handleOptionClick.bind(this);

        this.lastFeaturedImage;
        this.firstMedia;
        this.hasFirstMediaImg;

        this.fetchProduct = this.fetchProduct.bind(this);
        this.galleryObserver = this.galleryObserver.bind(this);
        this.observerCallback = this.observerCallback.bind(this);
        this.disconnectObserver = this.disconnectObserver.bind(this);
        this.removeButtonEventListener =
          this.removeButtonEventListener.bind(this);

        this.bindPrefetch();
        this.applyLayoutClass();
      }

      /* The drawer and the modal have a different closed position (off screen to
         the right / below the viewport), and the panel carries a permanent
         transform transition. Applying the layout class therefore animates the
         panel between the two closed positions - which is visible, because the
         drawer's closed state is off screen but not hidden. The panel is
         rendered with --static for that reason, and the class is applied while
         that is still in place. */
      applyLayoutClass() {
        const layout = this.add_button?.getAttribute("data-quick-add-layout");
        if (layout !== "modal") return;
        document
          .querySelector(".wt__quick-buy")
          ?.classList.add("wt__quick-buy--modal");
      }

      /* Put the panel in its closed position for this layout, then hand the
         transitions back so the opening animation starts from there. */
      prepareLayout(isModal) {
        this.quick_add_container.classList.add(
          "wt__quick-buy__container--static",
        );
        this.quick_add.classList.toggle("wt__quick-buy--modal", isModal);
        this.quick_add_container.getBoundingClientRect();
        this.quick_add_container.classList.remove(
          "wt__quick-buy__container--static",
        );
      }

      /* Start loading the product as soon as the shopper shows intent, so the
         click itself usually has nothing left to wait for. */
      bindPrefetch() {
        if (!this.add_button) return;

        const target = this.closest(".card__picture-container") || this;
        let timer = null;

        const start = () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            const url = this.add_button.getAttribute("data-product-url");
            if (url && !quickAddHtmlCache.has(url)) this.loadProductHtml(url);
          }, 120);
        };
        const cancel = () => clearTimeout(timer);

        target.addEventListener("pointerenter", start);
        target.addEventListener("pointerleave", cancel);
        this.add_button.addEventListener("touchstart", start, {
          passive: true,
        });
        this.add_button.addEventListener("focus", start);
      }

      loadProductHtml(url, signal) {
        const sectionId = readSectionId();

        const sectionRequest = sectionId
          ? fetch(quickAddSectionUrl(url, sectionId), { signal })
              .then((res) => (res.ok ? res.text() : ""))
              .then((html) => (html.includes("wt-product__info") ? html : null))
          : Promise.resolve(null);

        const request = sectionRequest
          .then((html) => {
            /* No section id known yet, or this product uses another template */
            return html || fetch(url, { signal }).then((res) => res.text());
          })
          .then((html) => {
            quickAddHtmlCache.set(url, html);
            return html;
          })
          .catch((error) => {
            quickAddHtmlCache.delete(url);
            throw error;
          });

        quickAddHtmlCache.set(url, request);
        return request;
      }

      requestProductHtml(url, signal) {
        const cached = quickAddHtmlCache.get(url);
        if (cached) return Promise.resolve(cached);
        return this.loadProductHtml(url, signal);
      }

      fetchProduct(product_url, { swap = false } = {}) {
        this.lastFeaturedImage = null;
        this.firstMedia = null;
        this.hasFirstMediaImg = null;
        this.quick_add = document.querySelector(".wt__quick-buy");
        this.quick_add_container = document.querySelector(
          ".wt__quick-buy__container",
        );
        this.quick_add_wrapper = document.querySelector(
          ".wt__quick-buy--wrapper",
        );
        this.quick_add_product = this.quick_add.querySelector(
          ".wt__quick-buy--product",
        );
        this.close_button = this.quick_add.querySelector(".close-btn");
        this.loader = this.quick_add.querySelector(".wt__quick-buy-loader");
        this.pending_loader = this.quick_add.querySelector(
          ".wt__quick-buy--pending-loader",
        );
        this.page_overlay = document.querySelector(
          ".wt__quick-buy--page-overlay",
        );
        this.body = document.body;

        this.controller?.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        const requestId = (this.requestId || 0) + 1;
        this.requestId = requestId;

        const isModal = this.layout === "modal";
        /* The modal is sized by its content, so opening it around the loader and
           only then filling it makes it resize under the user. Open it once the
           markup - and its image - are in place instead. */
        const deferOpen = isModal && !swap;

        if (swap) {
          /* Keep the product that is already on screen visible while the next one
             loads, so the panel never collapses to the height of the loader. */
          this.quick_add_container.classList.add(
            "wt__quick-buy__container--busy",
          );
          this.togglePendingLoader(true);
        } else {
          const allWtProducts =
            this.quick_add_wrapper.querySelectorAll(".wt-product");
          allWtProducts?.forEach((product) => product.remove());
          this.quick_add.classList.remove("hidden");
          this.prepareLayout(isModal);
          this.body.classList.add("quick-buy-page-overlay");
          this.body.classList.toggle("quick-buy-modal-open", isModal);
          this.page_overlay.classList.add("wt__quick-buy--page-overlay--open");

          if (deferOpen) {
            this.togglePendingLoader(true);
          } else {
            this.quick_add_container.classList.add(
              "wt__quick-buy__container--open",
            );
          }
        }

        this.bindPanelListeners();

        this.requestProductHtml(product_url, signal)
          .then(async (res) => {
            /* The panel was closed, or another product was requested, while this
               response was in flight. */
            if (requestId !== this.requestId) return;
            this.loader.classList.add("hidden");
            const htmlDocument = new DOMParser().parseFromString(
              res,
              "text/html",
            );
            this.productMain = htmlDocument.querySelector(".wt-product__main");
            this.productCard = htmlDocument.querySelector(".wt-product");
            this.productInfo = htmlDocument.querySelector(".wt-product__info");

            /* Remember which section holds the product, so the next quick view
               can request that section alone instead of the whole page. */
            const sectionWrapper = this.productCard?.closest(
              "[id^='shopify-section-']",
            );
            if (sectionWrapper) {
              storeSectionId(sectionWrapper.id.replace("shopify-section-", ""));
            }

            const animatedElements = this.productMain.querySelectorAll(
              ".scroll-trigger.animate--slide-in",
            );
            animatedElements?.forEach((element) =>
              element.classList.remove("scroll-trigger", "animate--slide-in"),
            );

            const productGallery =
            this.productCard.querySelector("gallery-section");


            this.removeElements(
              this.productCard,
              "collapsible-section",
              "[not-quick-add]",
              ".product__inventory",
              ".share-icons__container",
              "#gallery-loader",
              "pickup-availability",
              ".wt-product__sku",
              ".wt-product__feature-icons",
              "gallery-section",
            );

            const newVariantId =
              this.productCard.querySelector("variant-options");
            if (newVariantId) {
              newVariantId.dataset.updateUrl = "false";
              this.updateAttribute(
                newVariantId,
                "data-original-section",
                newVariantId.getAttribute("data-section"),
              );
              this.updateAttribute(
                newVariantId,
                "data-section",
                `quick-${newVariantId.getAttribute("data-section")}`,
              );
            }

          

            productGallery.style.display = "none";

            this.images = productGallery.querySelectorAll("img");
            this.updateAttribute(
              productGallery,
              "id",
              `MediaGallery-${newVariantId?.getAttribute("data-section")}`,
            );
            const firstImageFromGallery = productGallery.querySelector("li");
            if (firstImageFromGallery) {
              this.firstMedia = firstImageFromGallery;

              this.playBtn = this.firstMedia.querySelector(".model-btn");
              if (this.playBtn) this.playBtn.setAttribute("tabindex", "0");

              this.video = this.firstMedia.querySelector("video");
              if (this.video) this.video.setAttribute("tabindex", "0");

              this.hasFirstMediaImg = Boolean(
                firstImageFromGallery.querySelector("img"),
              );
            }

            const firstGalleryImage = firstImageFromGallery?.querySelector("img");
            if (firstGalleryImage) {
              firstGalleryImage.removeAttribute("srcset");
              firstGalleryImage.removeAttribute("sizes");
              firstGalleryImage.setAttribute("fetchpriority", "high");
              firstGalleryImage.setAttribute("loading", "eager");
              const gallerySrc = firstGalleryImage.getAttribute("src");
              if (gallerySrc) {
                firstGalleryImage.setAttribute(
                  "src",
                  quickAddImageUrl(gallerySrc, 720),
                );
              }
            }
            const imageContainer = document.createElement("div");
            imageContainer.setAttribute("class", "product-image");

            const productDetails = document.createElement("div");
            productDetails.setAttribute("class", "wt-product__details");

            const productAbout = document.createElement("div");
            productAbout.setAttribute("class", "product-info");
            const title = this.productInfo.querySelector(".wt-product__name");

            if (title) {
              const aElement = document.createElement("a");
              aElement.textContent = title.textContent;
              aElement.href = this.product_url;
              title.innerHTML = "";
              title.appendChild(aElement);
              title.setAttribute("tabindex", "0");
            }

            const productElementsToCopy = this.productInfo.querySelectorAll(
              ".wt-product__brand, .wt-product__name, .wt-rating, .wt-product__price, .product__tax",
            );

            productElementsToCopy.forEach((product) =>
              productAbout.appendChild(product),
            );

            imageContainer.innerHTML = firstImageFromGallery?.innerHTML || "";
            productDetails.appendChild(imageContainer);
            productDetails.appendChild(productAbout);
            this.productInfo.prepend(productDetails);

            this.galleryObserver();

            const mainProductPrice =
              this.productCard.querySelector(".wt-product__price");
            this.updateAttribute(
              mainProductPrice,
              "id",
              `price-${newVariantId?.getAttribute("data-section")}`,
            );

            const allFieldSets = this.productCard.querySelectorAll("fieldset");
            this.updateFieldSets(allFieldSets);

            const addBtn = this.productCard.querySelector('button[name="add"]');
            this.updateAttribute(
              addBtn,
              "id",
              `ProductSubmitButton-${newVariantId?.getAttribute("data-section")}`,
            );

            const form = this.productCard.querySelector(
              'form[method="post"][data-type="add-to-cart-form"]',
            );
            this.updateAttribute(
              form,
              "id",
              `product-form-${newVariantId?.getAttribute("data-section")}`,
            );

            const subWidget = this.productCard.querySelector('.wt-subscription-widget');
            if (subWidget) {
              const quickSectionId = newVariantId?.getAttribute("data-section");
              const originalSectionId = newVariantId?.getAttribute("data-original-section");
              subWidget.setAttribute('data-section-id', quickSectionId);
              subWidget.setAttribute('data-original-section-id', originalSectionId);
              subWidget.setAttribute('data-update-url-on-plan-change', 'false');

              const hiddenInput = subWidget.querySelector('input[name="selling_plan"]');
              if (hiddenInput) {
                hiddenInput.setAttribute('form', `product-form-${quickSectionId}`);
              }
            }

            /* Warm the product image before anything is inserted, so the panel is
               laid out at its final height in one go instead of growing once the
               image arrives. */
            const heroImageSrc = imageContainer
              .querySelector("img")
              ?.getAttribute("src");
            if (heroImageSrc) await this.preloadImage(heroImageSrc);
            if (requestId !== this.requestId) return;

            const viewAllDetailsContainer =
              this.quick_add_wrapper.querySelector(
                ".wt__quick-buy__view-all-container",
              );
            this.quick_add_wrapper.removeChild(viewAllDetailsContainer);
            /* On a swap the previous product was left in place while loading */
            this.quick_add_wrapper
              .querySelectorAll(".wt-product")
              .forEach((product) => product.remove());
            this.quick_add_wrapper.innerHTML += this.productCard.outerHTML;
            this.quick_add_wrapper.append(viewAllDetailsContainer);

            const giftCardInput = this.quick_add_wrapper.querySelector(
              '[name="properties[__shopify_send_gift_card_to_recipient]"]',
            );
            if (giftCardInput) giftCardInput.removeAttribute("disabled");

            this.addViewAllDetailsButton();
            this.isDrawerOpen = true;

            // Fire the quick-buy drawer–open event
            publish(PUB_SUB_EVENTS.quickBuyDrawerOpen, { source: "quick-add" });

            // initialize the variant options
            const variantOptions = this.quick_add_wrapper.querySelector("variant-options");
            if (variantOptions && typeof variantOptions.initialize === 'function') {
              variantOptions.initialize();
            }

            this.close_button.setAttribute("tabindex", "0");

            document.addEventListener("click", this.handleInteractionOutside);

            this.revealPanel({ deferOpen, swap });
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.error("Fetch aborted");
              return;
            }
            if (requestId !== this.requestId) return;
            console.error("Fetch error:", error);
            /* Nothing usable to show - fall back to the product page rather than
               leaving the shopper with an empty overlay. */
            this.togglePendingLoader(false);
            window.location.href = product_url;
          });
      }

      /* Reveal (or hand back) the panel only once its content has settled, so the
         shopper never sees it resize. */
      revealPanel({ deferOpen, swap }) {
        const reveal = () => {
          this.togglePendingLoader(false);
          this.quick_add_container.classList.remove(
            "wt__quick-buy__container--busy",
          );
          this.quick_add_container.classList.add(
            "wt__quick-buy__container--open",
          );
          if (!swap) this.close_button.focus();
        };

        if (!deferOpen && !swap) {
          reveal();
          return;
        }

        this.whenImagesReady().then(() => requestAnimationFrame(reveal));
      }

      preloadImage(src, timeout = 900) {
        return Promise.race([
          new Promise((resolve) => {
            const image = new Image();
            image.onload = resolve;
            image.onerror = resolve;
            image.src = src;
          }),
          new Promise((resolve) => setTimeout(resolve, timeout)),
        ]);
      }

      /* The product image is the only element whose height is not known up front */
      whenImagesReady(timeout = 900) {
        const images = Array.from(
          this.quick_add_wrapper.querySelectorAll(".product-image img"),
        ).filter((image) => !image.complete);

        if (images.length === 0) return Promise.resolve();

        return Promise.race([
          Promise.all(
            images.map(
              (image) =>
                new Promise((resolve) => {
                  image.addEventListener("load", resolve, { once: true });
                  image.addEventListener("error", resolve, { once: true });
                }),
            ),
          ),
          new Promise((resolve) => setTimeout(resolve, timeout)),
        ]);
      }

      togglePendingLoader(show) {
        this.isPending = show;
        this.pending_loader?.classList.toggle("hidden", !show);
        /* The panel is not on screen yet, so the backdrop is the only way out */
        if (show) {
          document.addEventListener("click", this.handleInteractionOutside);
        }
      }

      /* The panel is a single shared element, so every quick-add instance would
         otherwise stack its own listeners on it. */
      bindPanelListeners() {
        const previous = this.quick_add.quickAddPanelListeners;
        if (previous) {
          previous.close_button.removeEventListener("click", previous.onClose);
          previous.container.removeEventListener("keydown", previous.onKeydown);
          previous.wrapper.removeEventListener("click", previous.onOptionClick);
        }

        this.close_button.addEventListener("click", this.handleClose);
        this.quick_add_container.addEventListener("keydown", this.handleKeydown);
        this.quick_add_wrapper.addEventListener("click", this.handleOptionClick);

        this.quick_add.quickAddPanelListeners = {
          close_button: this.close_button,
          container: this.quick_add_container,
          wrapper: this.quick_add_wrapper,
          onClose: this.handleClose,
          onKeydown: this.handleKeydown,
          onOptionClick: this.handleOptionClick,
        };
      }

      unbindPanelListeners() {
        const bound = this.quick_add.quickAddPanelListeners;
        if (!bound) return;
        bound.close_button.removeEventListener("click", bound.onClose);
        bound.container.removeEventListener("keydown", bound.onKeydown);
        bound.wrapper.removeEventListener("click", bound.onOptionClick);
        this.quick_add.quickAddPanelListeners = null;
      }

      handleClose() {
        this.controller?.abort();
        this.closeCart();
        this.close_button.setAttribute("tabindex", "-1");
        this.currentTrigger?.focus();
      }

      handleKeydown(e) {
        const isTabPressed =
          e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
        const { first, last } = this.getFocusableElements(
          this.quick_add_container,
        );

        if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
          this.close_button.click();
        }

        if (isTabPressed) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }

      /* Sibling products (colour) are separate products rendered as links inside
         an option block. Inside the quick view they should swap the rendered
         product in place, the same way a regular variant does, instead of sending
         the shopper to the product page. */
      handleOptionClick(event) {
        const link = event.target.closest?.("a[href]");
        if (!link) return;
        if (!link.closest(".wt-product__option")) return;
        if (link.closest("variant-options")) return;
        if (link.classList.contains("active")) {
          event.preventDefault();
          return;
        }

        const url = link.getAttribute("href");
        if (!url || url.startsWith("#")) return;

        event.preventDefault();
        this.product_url = url;
        this.fetchProduct(url, { swap: true });
      }

      addViewAllDetailsButton() {
        const viewAllDetailsContainer = this.quick_add_wrapper.querySelector(
          ".wt__quick-buy__view-all-container",
        );
        const link = viewAllDetailsContainer.querySelector("a");
        link.href = this.product_url;
        link.setAttribute("tabindex", "0");
        viewAllDetailsContainer.classList.remove("hidden");
      }

      hideViewAllDetailsButton() {
        const viewAllDetailsContainer = this.quick_add_wrapper.querySelector(
          ".wt__quick-buy__view-all-container",
        );
        viewAllDetailsContainer.classList.add("hidden");
      }

      galleryObserver() {
        const config = {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        };
        this.disconnectObserver();
        this.observer = new MutationObserver(this.observerCallback);
        this.observer.observe(this.quick_add_wrapper, config);
      }

      observerCallback(mutations) {
        for (const mutation of mutations) {
          if (mutation.target.localName === "variant-options") {
            this.quick_add = document.querySelector(".wt__quick-buy");
            const variantOptions =
              this.quick_add.querySelector("variant-options");
            const imageContainer =
              this.quick_add.querySelector(".product-image");
            const image = imageContainer.querySelector("img");
            const formInput = this.quick_add.querySelector(
              'form input[name="id"]',
            );
            if (variantOptions.getAttribute("data-variant-id")) {
              formInput.value = variantOptions.getAttribute("data-variant-id");
              formInput.setAttribute(
                "value",
                variantOptions.getAttribute("data-variant-id"),
              );
            }
            if (
              variantOptions.getAttribute("data-featured-image") &&
              this.lastFeaturedImage !==
                variantOptions.getAttribute("data-featured-image")
            ) {
              image?.setAttribute(
                "src",
                variantOptions.getAttribute("data-featured-image"),
              );
              image?.removeAttribute("srcset");

              this.lastFeaturedImage = variantOptions.getAttribute(
                "data-featured-image",
              );
              if (
                imageContainer.children[0]?.nodeName !== "IMG" &&
                imageContainer.children[0]?.nodeName !== "A"
              ) {
                const newImage = document.createElement("img");
                newImage.setAttribute(
                  "class",
                  "wt-product__img  wt-product__img--zoom-cursor",
                );
                newImage.setAttribute(
                  "src",
                  variantOptions.getAttribute("data-featured-image"),
                );
                newImage.removeAttribute("srcset");
                imageContainer.innerHTML = "";
                imageContainer.appendChild(newImage);
              } else {
                image.setAttribute(
                  "src",
                  variantOptions.getAttribute("data-featured-image"),
                );
                image.removeAttribute("srcset");
              }
              this.lastFeaturedImage = variantOptions.getAttribute(
                "data-featured-image",
              );
            } else {
              const hasFirstMediaModel =
                this.firstMedia?.querySelector("model-element");
              if (!this.hasFirstMediaImg || hasFirstMediaModel)
                imageContainer.innerHTML = this.firstMedia?.innerHTML;
            }
            break;
          }
        }
      }

      handleInteractionOutside(event) {
        if (
          (this.isDrawerOpen || this.isPending) &&
          event.target === this.page_overlay
        ) {
          this.closeCart();
        }
      }

      closeCart() {
        // Fire the quick-buy drawer–close event
        publish(PUB_SUB_EVENTS.quickBuyDrawerClose, { source: "quick-add" });

        /* Drop any response still in flight so it cannot re-open the panel */
        this.requestId = (this.requestId || 0) + 1;
        this.controller?.abort();

        const loader = this.quick_add.querySelector(".wt__quick-buy-loader");
        loader.classList.remove("hidden");
        const product = this.quick_add.querySelector(".wt-product");

        if (product) product.remove();

        this.body.classList.remove("quick-buy-page-overlay");
        this.body.classList.remove("quick-buy-modal-open");
        this.page_overlay.classList.remove("wt__quick-buy--page-overlay--open");
        this.quick_add_container.classList.remove(
          "wt__quick-buy__container--open",
          "wt__quick-buy__container--busy",
        );
        this.togglePendingLoader(false);
        this.isDrawerOpen = false;
        this.hideViewAllDetailsButton();
        document.removeEventListener("click", this.handleInteractionOutside);
        this.unbindPanelListeners();
        this.disconnectObserver();
        this.removeButtonEventListener();
      }

      removeElements(element, ...selectors) {
        selectors.forEach((selector) => {
          const elements = element.querySelectorAll(selector);
          elements.forEach((el) => el.remove());
        });
      }

      updateAttribute(element, attribute, value) {
        if (element) {
          element.setAttribute(attribute, value);
        }
      }

      updateFieldSets(fieldSets) {
        fieldSets.forEach((fieldset) => {
          const inputRadios = fieldset.querySelectorAll('input[type="radio"]');
          const labels = fieldset.querySelectorAll("label");

          inputRadios.forEach((radio) => {
            this.updateAttribute(
              radio,
              "id",
              `quick-${radio.getAttribute("id")}`,
            );
            this.updateAttribute(
              radio,
              "form",
              `product-form-${radio.getAttribute("id")}`,
            );
          });

          labels.forEach((label) => {
            this.updateAttribute(
              label,
              "for",
              `quick-${label.getAttribute("for")}`,
            );
          });
        });
      }

      disconnectObserver() {
        this.observer?.disconnect();
      }

      removeButtonEventListener() {
        if (this.add_button)
          this.add_button.removeEventListener("click", () =>
            this.fetchProduct(this.product_url),
          );
      }

      disconnectedCallback() {
        this.disconnectObserver();
        this.removeButtonEventListener();
      }
    },
  );
}
